import { AppMark } from "../components/AppMark";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/Toast";
import { BottomNav } from "../components/BottomNav";
import { Modal } from "../components/Modal";
import { WorkTypesTab } from "../components/dashboard/WorkTypesTab";
import { PayTab } from "../components/dashboard/PayTab";
import { PayPlannerTab } from "../components/dashboard/PayPlannerTab";
import { TimeTodayPanel } from "../components/dashboard/TimeTodayPanel";
import { RunCard, type RunEditFields } from "../components/RunCard";
import { LiveNowPanel } from "../components/dashboard/LiveNowPanel";
import { LineStationsModal } from "../components/dashboard/LineStationsModal";
import { useUnits } from "../hooks/useUnits";
import { fmtRate, fmtWeight, parseWeightInput, rateParts, weightParts, type UnitSystem } from "../lib/units";
import { getAuthToken, getCurrentOrg } from "../lib/storage";
import { useLineStations } from "../lib/line";
import {
  NUG_SIZES, TRIM_TYPES, type DashboardData, type Harvest, type Job, type RunRow, type Strain,
} from "../lib/types";

// Manager dashboard — ported from legacy dashboard.html's tab layout, minus
// the Flags tab, the manual "Add run" modal, and merge mode (not in this
// milestone's brief; the run-edit-lite + tombstone-delete surfaces that ARE
// in scope live on RunCard). "By harvest" is a NEW read-only stats tab (the
// per_harvest aggregate from GET /api/dashboard); "Harvests" is the
// separate create/list CRUD tab against GET/POST /api/harvests.

type Tab = "today" | "all" | "players" | "strains" | "byHarvest" | "harvests" | "jobs" | "workTypes" | "pay" | "payPlan";

const TABS: { key: Tab; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "all", label: "All runs" },
  { key: "players", label: "By trimmer" },
  { key: "strains", label: "By strain" },
  { key: "byHarvest", label: "By harvest" },
  { key: "harvests", label: "Harvests" },
  { key: "jobs", label: "Jobs" },
  { key: "workTypes", label: "Work types" },
  { key: "pay", label: "Pay" },
  { key: "payPlan", label: "Pay planner" },
];

function fmtDuration(ms: number): string {
  const h = Math.floor((ms || 0) / 3600000);
  const m = Math.floor(((ms || 0) % 3600000) / 60000);
  return h ? `${h}h ${m}m` : `${m}m`;
}

function DailyChart({ daily, units }: { daily: DashboardData["daily"]; units: UnitSystem }) {
  if (!daily.length) return <div className="muted small">No data yet</div>;
  const max = Math.max(...daily.map((d) => d.grams), 1);
  return (
    <div style={{
      display: "flex", alignItems: "flex-end", gap: 6, height: 180,
      borderBottom: "1px solid var(--border)", paddingBottom: 5,
    }}
    >
      {daily.map((d) => (
        <div
          key={d.day}
          style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "flex-end", height: "100%",
          }}
        >
          <div className="muted" style={{ fontSize: "0.7rem", marginBottom: 3 }}>{weightParts(d.grams, units, 0).value}</div>
          <div style={{
            width: "100%", maxWidth: 36, borderRadius: "4px 4px 0 0", minHeight: 2,
            height: `${(d.grams / max) * 100}%`,
            background: "linear-gradient(180deg, var(--primary), var(--primary-strong))",
          }}
          />
          <div className="muted" style={{ fontSize: "0.65rem", marginTop: 4 }}>{d.day.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

// M6 wave-2: shows a "Line" pill on a job's row once it has ≥1 station
// configured — shares LineStationsModal's react-query cache key, so saving
// stations there updates this badge with no reload needed.
function JobLineBadge({ jobId }: { jobId: number }) {
  const { data } = useLineStations(jobId);
  if (!data?.length) return null;
  return <span className="pill live-pill" data-testid="job-line-badge">Line</span>;
}

function JobsTab() {
  const units = useUnits();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [strain, setStrain] = useState("");
  const [style, setStyle] = useState("");
  const [size, setSize] = useState("");
  const [creating, setCreating] = useState(false);
  const [lineJobId, setLineJobId] = useState<number | null>(null);

  const { data: jobs, isLoading } = useQuery({
    queryKey: ["jobs-dashboard"],
    queryFn: () => api.get("/api/jobs").then((r) => readJson<Job[]>(r)),
  });
  const { data: strains } = useQuery({
    queryKey: ["strains"],
    queryFn: () => api.get("/api/strains").then((r) => readJson<Strain[]>(r)),
  });

  async function createJob() {
    const targetNum = Number(target);
    if (!name.trim() || !Number.isFinite(targetNum) || targetNum <= 0) {
      toast("Name and a positive target weight are required", "err");
      return;
    }
    setCreating(true);
    const res = await api.postJson("/api/jobs", {
      name: name.trim(), target_grams: parseWeightInput(targetNum, units),
      strain: strain || null, trim_type: style || null, nug_size: size || null,
    });
    setCreating(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not create job", "err"); return; }
    toast("Job created — the crew will see it on the strain step", "ok");
    setShowNew(false);
    setName(""); setTarget(""); setStrain(""); setStyle(""); setSize("");
    queryClient.invalidateQueries({ queryKey: ["jobs-dashboard"] });
  }

  async function setStatus(id: number, status: "open" | "closed") {
    const res = await api.patchForm(`/api/jobs/${id}`, { status });
    if (res.ok) {
      toast(status === "open" ? "Job reopened" : "Job closed", "ok");
      queryClient.invalidateQueries({ queryKey: ["jobs-dashboard"] });
    } else {
      toast("Update failed", "err");
    }
  }

  return (
    <div className="panel">
      <h2>
        Trim jobs
        <button
          type="button" className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }}
          data-testid="new-job-open" onClick={() => setShowNew(true)}
        >
          + New job
        </button>
      </h2>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Pre-configure a pile of work — strain, trim style, nug size, target weight. Everyone&rsquo;s runs
        chip away at it; when the target lands, the crew gets badges.
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr><th>Job</th><th>Progress</th><th>Rate</th><th>ETA</th><th>Crew</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="muted">Loading…</td></tr>}
            {!isLoading && !jobs?.length && <tr><td colSpan={7} className="muted">No jobs yet — create one.</td></tr>}
            {(jobs || []).map((j) => {
              const eta = j.eta_minutes == null ? "—"
                : j.eta_minutes >= 90 ? `${(j.eta_minutes / 60).toFixed(1)} h`
                  : `${j.eta_minutes} min`;
              const meta = [j.strain_name, j.trim_type, j.nug_size].filter(Boolean).join(" · ");
              return (
                <tr key={j.id} data-testid="job-row">
                  <td>
                    <b>{j.name}</b> <JobLineBadge jobId={j.id} />
                    {meta && <div className="muted small">{meta}</div>}
                  </td>
                  <td style={{ minWidth: 160 }}>
                    <div className="progress-track">
                      <div
                        className={`progress-fill${j.status === "done" ? " done" : j.status === "open" && j.pct >= 90 ? " endgame" : ""}`}
                        style={{ width: `${j.pct}%` }}
                      />
                    </div>
                    <div className="muted small">{units === "metric" ? `${j.done_grams.toFixed(0)} / ${j.target_grams.toFixed(0)}g` : `${fmtWeight(j.done_grams, units)} / ${fmtWeight(j.target_grams, units)}`} · {j.pct}%</div>
                    {j.status === "open" && j.pct >= 90 && (
                      <div className="job-countdown" data-testid="job-endgame-chip">
                        {[
                          j.eta_minutes != null ? `~${j.eta_minutes} min to finish` : null,
                          `${fmtWeight(j.remaining_grams, units, 0)} to go`,
                        ].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td>{j.rate_gpm ? (units === "metric" ? `${j.rate_gpm} g/min` : fmtRate(j.rate_gpm, units)) : "—"}</td>
                  <td>{j.status === "open" ? eta : "—"}</td>
                  <td>{j.crew || 0}</td>
                  <td>
                    <span className={`pill${j.status === "open" ? " live-pill" : ""}`}>
                      {j.status === "done" ? "🏁 done" : j.status}
                    </span>
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: "flex-end", gap: 6 }}>
                      <button
                        type="button" className="btn btn-ghost btn-sm"
                        data-testid={`line-open-${j.id}`} onClick={() => setLineJobId(j.id)}
                      >
                        Line
                      </button>
                      {j.status !== "done" && (
                        <button
                          type="button" className="btn btn-ghost btn-sm"
                          onClick={() => setStatus(j.id, j.status === "open" ? "closed" : "open")}
                        >
                          {j.status === "open" ? "Close" : "Reopen"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {lineJobId !== null && (() => {
        const lineJob = (jobs || []).find((j) => j.id === lineJobId);
        return lineJob ? <LineStationsModal job={lineJob} onClose={() => setLineJobId(null)} /> : null;
      })()}

      {showNew && (
        <Modal title="New job" onClose={() => setShowNew(false)}>
          <div className="field">
            <label className="field-label" htmlFor="job-name">Name</label>
            <input id="job-name" data-testid="new-job-name" value={name} maxLength={60}
              placeholder="e.g. Round 12 — Purple Static" onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="job-target">{units === "imperial" ? "Target oz" : "Target grams"}</label>
            <input id="job-target" data-testid="new-job-target" type="number" min="0" step="1" value={target}
              placeholder={units === "imperial" ? "e.g. 176" : "e.g. 5000"} onChange={(e) => setTarget(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="job-strain">Strain (blank = any)</label>
            <select id="job-strain" value={strain} onChange={(e) => setStrain(e.target.value)}>
              <option value="">— trimmer picks —</option>
              {(strains || []).map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="job-style">Trim style (blank = trimmer picks)</label>
            <select id="job-style" value={style} onChange={(e) => setStyle(e.target.value)}>
              <option value="">— trimmer picks —</option>
              {TRIM_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label" htmlFor="job-size">Average nug size (blank = trimmer picks)</label>
            <select id="job-size" value={size} onChange={(e) => setSize(e.target.value)}>
              <option value="">— trimmer picks —</option>
              {NUG_SIZES.map((n) => <option key={n} value={n}>{n[0].toUpperCase() + n.slice(1)}</option>)}
            </select>
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" data-testid="new-job-submit" disabled={creating} onClick={createJob}>
              Create job
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function HarvestsTab() {
  const units = useUnits();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState<number | null>(null);

  // PATCH /api/harvests/:id reads JSON-or-form server-side (same as legacy),
  // so the form helper is fine. Closing only stops new runs auto-attaching —
  // existing runs and totals are untouched, which is why there's no confirm.
  async function toggleActive(h: Harvest) {
    setToggling(h.id);
    try {
      const res = await api.patchForm(`/api/harvests/${h.id}`, { active: h.active ? 0 : 1 });
      if (!res.ok) {
        toast(`Couldn't ${h.active ? "close" : "reopen"} ${h.name}`, "err");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["harvests"] });
      toast(`${h.name} ${h.active ? "closed" : "reopened"}`, "ok");
    } finally {
      setToggling(null);
    }
  }

  const { data: harvests, isLoading } = useQuery({
    queryKey: ["harvests"],
    queryFn: () => api.get("/api/harvests").then((r) => readJson<Harvest[]>(r)),
  });

  async function create() {
    if (!name.trim()) { toast("Name is required", "err"); return; }
    setCreating(true);
    const res = await api.postJson("/api/harvests", { name: name.trim(), notes: notes.trim() || null });
    setCreating(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not create harvest", "err"); return; }
    toast("Harvest created — new runs attach to it", "ok");
    setShowNew(false);
    setName(""); setNotes("");
    queryClient.invalidateQueries({ queryKey: ["harvests"] });
  }

  return (
    <div className="panel">
      <h2>
        Harvests
        <button
          type="button" className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }}
          data-testid="new-harvest-open" onClick={() => setShowNew(true)}
        >
          + New harvest
        </button>
      </h2>
      <div className="small muted" style={{ marginBottom: 10 }}>
        New runs auto-attach to the newest open harvest.
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Harvest</th><th>Status</th><th>Runs</th><th>{units === "imperial" ? "Total" : "Total g"}</th><th /></tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
            {!isLoading && !harvests?.length && (
              <tr><td colSpan={5} className="muted">No harvests yet — create one so runs group up.</td></tr>
            )}
            {(harvests || []).map((h) => (
              <tr key={h.id}>
                <td>
                  <b>{h.name}</b>
                  {h.notes && <div className="muted small">{h.notes}</div>}
                </td>
                <td><span className={`pill${h.active ? " live-pill" : ""}`}>{h.active ? "open" : "closed"}</span></td>
                <td>{h.runs}</td>
                <td>{fmtWeight(h.total_grams, units, 0)}</td>
                <td>
                  <button
                    type="button" className="btn btn-sm"
                    data-testid={`harvest-toggle-${h.id}`}
                    disabled={toggling === h.id}
                    onClick={() => toggleActive(h)}
                  >
                    {toggling === h.id ? "…" : h.active ? "Close" : "Reopen"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <Modal title="New harvest" onClose={() => setShowNew(false)}>
          <div className="field">
            <label className="field-label" htmlFor="harvest-name">Name</label>
            <input id="harvest-name" data-testid="new-harvest-name" value={name} maxLength={60}
              placeholder="e.g. F2 — Aug 2026" onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="harvest-notes">Notes (optional)</label>
            <input id="harvest-notes" value={notes} maxLength={300} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" data-testid="new-harvest-submit" disabled={creating} onClick={create}>
              Create
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function DashboardPage() {
  const units = useUnits();
  const rateUnit = rateParts(0, units).unit;
  const { role, loading } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("today");
  const [includeSuperseded, setIncludeSuperseded] = useState(false);

  const isManagerPlus = role === "manager" || role === "admin";

  const { data: dash } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/api/dashboard").then((r) => readJson<DashboardData>(r)),
    enabled: isManagerPlus,
    refetchInterval: 20000,
  });

  const { data: allRuns, isLoading: allLoading } = useQuery({
    queryKey: ["runs-all", includeSuperseded],
    queryFn: () => api.get(`/api/runs?limit=100${includeSuperseded ? "&include=superseded" : ""}`)
      .then((r) => readJson<RunRow[]>(r)),
    enabled: isManagerPlus && tab === "all",
  });

  // Payroll is owner-only server-side: probe once and hide the Pay tab on 401/403.
  const { data: canPay } = useQuery({
    queryKey: ["payroll-access"],
    queryFn: () => api.get("/api/payroll").then((r) => r.ok),
    enabled: isManagerPlus,
    staleTime: Infinity,
    retry: false,
  });

  async function saveRunEdit(id: number, fields: RunEditFields) {
    const res = await api.patchForm(`/api/runs/${id}`, fields);
    if (res.ok) {
      toast("Run updated", "ok");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["runs-all"] });
    } else {
      const body = await res.json().catch(() => ({}));
      toast(body.error || "Save failed", "err");
    }
  }

  async function deleteRun(id: number) {
    if (!confirm(`Delete run #${id} permanently? This cannot be undone.`)) return;
    const res = await api.del(`/api/runs/${id}`);
    if (res.ok) {
      toast("Run deleted", "ok");
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["runs-all"] });
    } else {
      toast("Delete failed", "err");
    }
  }

  function exportCsv() {
    const orgId = getCurrentOrg();
    const token = orgId !== null ? getAuthToken(orgId) : null;
    const qs = new URLSearchParams({ what: "runs" });
    if (token) qs.set("token", token);
    window.open(`/dashboard/export.csv?${qs.toString()}`, "_blank");
  }

  return (
    <>
      <header className="top-bar">
        <div className="brand"><AppMark />trim log. <small>dashboard</small></div>
        <div className="top-actions">
          {isManagerPlus && (
            <button type="button" className="link-btn" data-testid="export-csv" onClick={exportCsv}>
              Export CSV
            </button>
          )}
        </div>
      </header>

      {loading && <main className="page"><div className="muted center" style={{ paddingTop: 80 }}>Loading…</div></main>}

      {!loading && !isManagerPlus && (
        <main className="page">
          <div className="panel center muted">Manager sign-in required to view this page.</div>
        </main>
      )}

      {!loading && isManagerPlus && (
        <main className="dash-grid">
          <section>
            <LiveNowPanel />
            <TimeTodayPanel />

            <div className="tab-bar">
              {TABS.filter((t) => (t.key !== "pay" && t.key !== "payPlan") || canPay).map((t) => (
                <button
                  key={t.key} type="button" data-testid={`tab-${t.key}`}
                  className={`tab-btn${tab === t.key ? " active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "today" && (
              <div className="panel">
                <h2>Today <span className="pill">{dash?.today.length ?? "—"}</span></h2>
                {(dash?.today || []).map((r) => (
                  <RunCard key={r.id} run={r} onSave={saveRunEdit} onDelete={deleteRun} />
                ))}
                {dash && dash.today.length === 0 && <div className="muted small">No runs yet today.</div>}
              </div>
            )}

            {tab === "all" && (
              <div className="panel">
                <h2>
                  Recent runs <span className="pill">{allRuns?.length ?? "—"}</span>
                </h2>
                <label className="check-row" style={{ marginBottom: 10 }}>
                  <input
                    type="checkbox" data-testid="include-superseded-toggle" checked={includeSuperseded}
                    onChange={(e) => setIncludeSuperseded(e.target.checked)}
                  />
                  <span>Show superseded &amp; deleted runs</span>
                </label>
                {allLoading && <div className="muted small">Loading…</div>}
                {(allRuns || []).map((r) => (
                  <RunCard key={r.id} run={r} onSave={saveRunEdit} onDelete={deleteRun} />
                ))}
                {!allLoading && allRuns?.length === 0 && <div className="muted small">No runs yet.</div>}
              </div>
            )}

            {tab === "players" && (
              <div className="panel">
                <h2>By trimmer</h2>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr><th>Trimmer</th><th>Runs</th><th>{units === "imperial" ? "Total" : "Total g"}</th><th>Time</th><th>Avg {rateUnit}</th><th>Avg QA</th><th>Last run</th></tr>
                    </thead>
                    <tbody>
                      {(dash?.per_player || []).map((p) => (
                        <tr key={p.id}>
                          <td>{p.name}</td>
                          <td>{p.runs}</td>
                          <td>{units === "imperial" ? fmtWeight(p.total_grams, units) : p.total_grams.toFixed(1)}</td>
                          <td>{fmtDuration(p.total_active_ms)}</td>
                          <td>{rateParts(p.avg_gpm, units, 1).value}</td>
                          <td>{p.avg_qa ? p.avg_qa.toFixed(1) : "—"}</td>
                          <td>{p.last_run ? fmtDuration(Date.now() - p.last_run * 1000) + " ago" : "—"}</td>
                        </tr>
                      ))}
                      {!dash?.per_player.length && <tr><td colSpan={7} className="muted">No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "strains" && (
              <div className="panel">
                <h2>By strain</h2>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr><th>Strain</th><th>Runs</th><th>Total start</th><th>Total end</th><th>Avg yield</th><th>Avg QA</th></tr>
                    </thead>
                    <tbody>
                      {(dash?.per_strain || []).map((s) => (
                        <tr key={s.id}>
                          <td>{s.name}</td>
                          <td>{s.runs}</td>
                          <td>{fmtWeight(s.total_start, units, 0)}</td>
                          <td>{fmtWeight(s.total_end, units, 0)}</td>
                          <td>{s.avg_yield ? `${s.avg_yield.toFixed(1)}%` : "—"}</td>
                          <td>{s.avg_qa ? s.avg_qa.toFixed(1) : "—"}</td>
                        </tr>
                      ))}
                      {!dash?.per_strain.length && <tr><td colSpan={6} className="muted">No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "byHarvest" && (
              <div className="panel">
                <h2>By harvest</h2>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr><th>Harvest</th><th>Status</th><th>Runs</th><th>{units === "imperial" ? "Total" : "Total g"}</th><th>Time</th><th>Avg yield</th><th>Avg QA</th></tr>
                    </thead>
                    <tbody>
                      {(dash?.per_harvest || []).map((h) => (
                        <tr key={h.id}>
                          <td>{h.name}</td>
                          <td><span className={`pill${h.active ? " live-pill" : ""}`}>{h.active ? "open" : "closed"}</span></td>
                          <td>{h.runs}</td>
                          <td>{fmtWeight(h.total_grams, units, 0)}</td>
                          <td>{fmtDuration(h.total_active_ms)}</td>
                          <td>{h.avg_yield ? `${h.avg_yield.toFixed(1)}%` : "—"}</td>
                          <td>{h.avg_qa ? h.avg_qa.toFixed(1) : "—"}</td>
                        </tr>
                      ))}
                      {!dash?.per_harvest.length && <tr><td colSpan={7} className="muted">No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "harvests" && <HarvestsTab />}
            {tab === "jobs" && <JobsTab />}
            {tab === "workTypes" && <WorkTypesTab />}
            {tab === "pay" && canPay && <PayTab />}
            {tab === "payPlan" && canPay && <PayPlannerTab />}
          </section>

          <aside>
            <div className="panel">
              <h2>Totals</h2>
              <div className="stat-row">
                <div className="stat"><div className="stat-val">{dash?.totals.runs ?? "—"}</div><div className="stat-lbl">Runs</div></div>
                <div className="stat"><div className="stat-val">{dash ? fmtWeight(dash.totals.total_grams, units, 0) : units === "imperial" ? "—" : "—g"}</div><div className="stat-lbl">{units === "imperial" ? "Total weight" : "Total grams"}</div></div>
                <div className="stat"><div className="stat-val">{dash ? (dash.totals.total_ms / 3600000).toFixed(1) : "—"}h</div><div className="stat-lbl">Total time</div></div>
                <div className="stat"><div className="stat-val">{dash ? rateParts(dash.totals.avg_gpm, units, 1).value : "—"}</div><div className="stat-lbl">Avg {rateUnit}</div></div>
                <div className="stat"><div className="stat-val">{dash?.totals.avg_qa ? dash.totals.avg_qa.toFixed(1) : "—"}</div><div className="stat-lbl">Avg QA</div></div>
              </div>
            </div>

            <div className="panel">
              <h2>30-day output{units === "imperial" ? " (oz)" : ""}</h2>
              <DailyChart daily={dash?.daily || []} units={units} />
            </div>

            <div className="panel">
              <h2>System</h2>
              <div className="small" style={{ color: dash?.ai_on ? "var(--primary)" : "var(--warning)" }}>
                ● AI {dash?.ai_on ? "on" : "off"}
              </div>
              <div className="muted small">
                {dash?.ai_on
                  ? "Scale OCR + QA vision active."
                  : "Set an AI key env var and restart to enable scale-cam OCR/QA."}
              </div>
            </div>
          </aside>
        </main>
      )}

      <BottomNav />
    </>
  );
}
